import axios from 'axios';
import { SHARES_TABLE_PAGE_SIZE, TESTS_TABLE_PAGE_SIZE } from './constants';
export const APPLICATION_CODE = 'ODYSSEY';
const statusesWithoutNewAndValidated = [
    'STARTED',
    'COMPLETED',
    'LATE_HANGUP',
    'USER_HUNG_UP',
    'ERROR',
    'NOT_SCORABLE',
    'PARTIALLY_SCORED',
    'SCORED',
    'CERTIFICATION_FAILED',
    'CERTIFICATION_REVOKED',
    'CERTIFIED',
    'ABORTED'
];
const statuses = statusesWithoutNewAndValidated.reduce((prev, current, index) => prev.concat(`${index === 0 ? '' : '&'}status=${current}`), '?');
function createStatusFilter(statuses) {
    return 'status=' + statuses.join('&status=');
}
const serialize = function (obj) {
    let encodedParts = [];
    for (const property in obj)
        if (obj.hasOwnProperty(property)) {
            const value = obj[property];
            Array.isArray(value)
                ? value.forEach((innerProp) => encodedParts.push(encodeURIComponent(property) + '=' + encodeURIComponent(innerProp)))
                : encodedParts.push(encodeURIComponent(property) + '=' + encodeURIComponent(value));
        }
    return encodedParts.join('&');
};
const attachSessionId = (event, sessionId) => {
    if (Array.isArray(event)) {
        return event.map((p) => ({ ...p, payload: { ...p.payload, sessionId } }));
    }
    return { ...event, payload: { ...event.payload, sessionId } };
};
export const makeSOPClient = ({ httpProvider, makeUrl, getToken, setToken, deleteToken }) => {
    return {
        session: {
            getToken,
            setToken,
            deleteToken
        },
        httpProvider,
        health: () => axios.get(makeUrl('/api/v1/health')),
        getAvailableProducts: () => httpProvider.get(makeUrl('/api/products?practice=false')),
        getAvailableProductsByApplicationCode: (applicationId = '') => httpProvider.get(makeUrl(`/api/products/${applicationId}?practice=false`)),
        getUserData: async () => httpProvider.get(makeUrl('/api/testTaker')),
        getToasts: async () => httpProvider.get(makeUrl('/api/testTaker/notifications/toasts')),
        markToastAsReceived: async (id) => httpProvider.post(makeUrl(`/api/testTaker/notifications/${id}/received`)),
        signIn: async (email, password, captchaResult) => {
            const body = {
                email,
                password,
                applicationCode: APPLICATION_CODE,
                captchaResult: captchaResult || undefined
            };
            return httpProvider.post(makeUrl('/api/authentication/login'), body);
        },
        resendVerificationEmail: async (email) => {
            const body = {
                email,
                applicationCode: APPLICATION_CODE
            };
            return httpProvider.post(makeUrl('/api/testTaker/email/revalidate'), body);
        },
        validateEmail: async (token) => {
            const body = {
                token,
                applicationCode: APPLICATION_CODE
            };
            return httpProvider.post(makeUrl('/api/authentication/email/validate'), body);
        },
        signUp: async (email, password, captchaResult, metadata) => {
            const body = {
                email,
                password,
                captchaResult,
                metadata,
                applicationCode: APPLICATION_CODE
            };
            return httpProvider.post(makeUrl('/api/testTaker'), body);
        },
        logout: async () => httpProvider.post(makeUrl('/api/authentication/logout')),
        refreshToken: async () => {
            const body = { token: getToken() };
            return httpProvider.post(makeUrl('/api/authentication/refresh'), body);
        },
        generateResetPasswordLink: async (email, captchaResult) => {
            const body = captchaResult ? { email, captchaResult } : { email };
            return httpProvider.post(makeUrl('/api/testTaker/password/reset'), body);
        },
        validatePassword: async (password) => httpProvider.post(makeUrl('/api/authentication/password/validate'), { password }),
        getUserBadges: async () => httpProvider.get(makeUrl('/api/testTaker/badges')),
        uploadSurvey: async (licenseId, payload) => httpProvider.post(makeUrl(`/api/licenses/${licenseId}/metadata`), payload),
        issueAccessToken: async (licenseId) => {
            const { data } = await httpProvider.get(makeUrl(`/api/licenses/${licenseId}/token`));
            return data;
        },
        exchangeAccessTokenForSopToken: async (accessToken) => {
            const { data } = await httpProvider.get(makeUrl(`/api/licenses/tokens/${accessToken}/user`));
            return data;
        },
        exchangeAccessTokenForTestToken: async (accessToken) => {
            const { data } = await httpProvider.get(makeUrl(`/api/licenses/tokens/${accessToken}/test`));
            return data;
        },
        sendLicenseSessionEvent: async (licenseId, payload, sessionId, sopToken) => httpProvider.post(makeUrl(`/api/licenses/${licenseId}/events`), attachSessionId(payload, sessionId), {
            headers: {
                Authorization: `Bearer ${sopToken}`
            }
        }),
        registerForEventStreamAsTestTaker: async () => {
            const { data } = await httpProvider.get(makeUrl('/api/testTaker/stream/register'));
            return data;
        },
        getLanguages: async (query = '', pageSize = 7) => httpProvider.get(makeUrl(`/api/dictionaries/languages?name=${query}&pageSize=${pageSize}`)),
        getCountries: async (query = '', pageSize = 7) => httpProvider.get(makeUrl(`/api/dictionaries/countries?name=${query}&applicationCode=${APPLICATION_CODE}&pageSize=${pageSize}`)),
        getCities: async (query, countryId) => httpProvider.get(makeUrl(`/api/dictionaries/cities?countryId=${countryId}&name=${query}&pageSize=7`)),
        updatePIIData: async (user, testToken) => httpProvider.post(makeUrl('/api/licenses/verification/id'), user, {
            headers: { Authorization: `Bearer ${testToken}` }
        }),
        updateProfile: async (user) => httpProvider.patch(makeUrl('/api/testTaker'), user),
        addMetadataToProfile: async (meta) => httpProvider.put(makeUrl('/api/testTaker/profile'), { set: meta }),
        changePassword: async (oldPassword, newPassword, confirmPassword) => httpProvider.put(makeUrl('/api/testTaker/password/update'), {
            oldPassword,
            newPassword,
            confirmPassword
        }),
        setNewPassword: async (newPassword, token) => httpProvider.put(makeUrl('/api/testTaker/password'), {
            newPassword,
            token
        }),
        getUserTransactionProducts: async () => httpProvider.get(makeUrl('/api/testTaker/transactions/products?transactionStatus=FULFILLED')),
        getUserIPCountry: async () => {
            let userCountry = 'US';
            let blocked = false; // change to true after backend implementation: https://pearson.atlassian.net/browse/VERSART-4947
            try {
                const res = await httpProvider.get(makeUrl('/api/dictionaries/countries/guess'));
                if (res.data.isoCode)
                    userCountry = res.data.isoCode;
                if (res.data.blocked)
                    blocked = res.data.blocked;
            }
            catch (err) {
                console.log('Error trying to determine user location based on IP.', err);
                throw err;
            }
            return { country: userCountry, blocked };
        },
        getPracticeTests: async () => httpProvider.get(makeUrl('/api/products?practice=true')),
        getMyTopScore: async (mtsCode = '') => httpProvider.get(makeUrl(`/api/testTaker/topscores/${mtsCode}`)),
        getMyTopScoreDetails: async (id, lang = 'ENG') => httpProvider.get(makeUrl(`/api/testTaker/topscores/${id}/certificate?language=${lang}`)),
        getMyTopScoreShares: async (id) => httpProvider.get(makeUrl(`/api/testTaker/topscores/${id}/shares`)),
        shareMyTopScore: async (data, id) => httpProvider.post(makeUrl(`/api/testTaker/topscores/${id}/shares`), data),
        myTopScoreEmailPreview: async (data, id) => httpProvider.post(makeUrl(`/api/testTaker/topscores/${id}/shares/preview`), data),
        downloadMyTopScoreCertificate: async (id, language = 'ENG') => httpProvider.get(makeUrl(`/api/testTaker/topscores/${id}/certificate.pdf?language=${language}`), {
            responseType: 'blob'
        }),
        downloadSharesCertificate: async (lang, code, pin) => httpProvider.get(makeUrl(`/api/shares/${code}/${pin}/certificate.pdf?language=${lang}`), {
            responseType: 'blob'
        }),
        getSentScores: async (page = 0, query) => httpProvider.get(makeUrl(`/api/shares?page=${page}&pageSize=${SHARES_TABLE_PAGE_SIZE}&name=${query || ''}`)),
        getSharesOrganizations: async (search) => httpProvider.get(makeUrl(`/api/shares/organizations?search=${search}`)),
        getProgramsWithSharedResultsByLicenseId: async (licenseId, page, pageSize) => httpProvider.get(makeUrl(`/api/shares/srw/licenses/${licenseId}/programs?page=${page}&pageSize=${pageSize}`)),
        shareTestResultWithProgramByLicenseId: async (programsList, licenseId) => httpProvider.put(makeUrl(`/api/shares/srw/licenses/${licenseId}/programs`), programsList),
        revokeSharing: async (id) => httpProvider.get(makeUrl(`/api/shares/${id}/revoke`)),
        certificateLookup: async (code, data) => httpProvider.post(makeUrl(`/api/shares/${code}/lookup`), data),
        displayCertificate: async (code, pin) => httpProvider.get(makeUrl(`/api/shares/${code}/${pin}/responses`)),
        displayCertificateData: async (code, pin) => httpProvider.get(makeUrl(`/api/shares/${code}/${pin}`)),
        loadCertificate: async (code, pin, lang) => httpProvider.get(makeUrl(`/api/shares/${code}/${pin}/certificate?language=${lang}`)),
        downloadResourceData: async (responseId, type, code, pin, resourceKey) => httpProvider.get(makeUrl(`/api/shares/${code}/${pin}/responses/${responseId}/${type}/${resourceKey}`), { responseType: type === 'audio' ? 'blob' : 'json' }),
        revokeMultipleShares: async (dto) => httpProvider.put(makeUrl('/api/shares/revoke'), dto),
        getSharesById: async (page = 0, resourceId, resourceName, query) => httpProvider.get(makeUrl(`/api/shares?page=${page}&pageSize=${SHARES_TABLE_PAGE_SIZE}&${resourceName}=${resourceId}${query ? '&email=' + query : ''}`)),
        getLicenses: (page = 0, name) => {
            const params = {
                name: name || '',
                page: page,
                pageSize: TESTS_TABLE_PAGE_SIZE,
                practice: false,
                sort: 'endTime',
                order: 'desc'
            };
            return httpProvider.get(makeUrl(`/api/licenses${statuses}`), { params });
        },
        getPracticeLicenses: async (page = 0, name) => {
            const statusFilter = createStatusFilter([
                'COMPLETED',
                'ERROR',
                'NOT_SCORABLE',
                'PARTIALLY_SCORED',
                'SCORED'
            ]);
            return httpProvider.get(makeUrl(`/api/licenses?name=${name || ''}&sort=endTime&order=desc&page=${page}&pageSize=${TESTS_TABLE_PAGE_SIZE}&practice=true&${statusFilter}`));
        },
        getLatestLicense: async () => httpProvider.get(makeUrl('/api/licenses?sort=endTime&order=desc&practice=false')),
        getLatestPracticeLicense: async () => {
            const statusFilter = createStatusFilter([
                'COMPLETED',
                'ERROR',
                'NOT_SCORABLE',
                'PARTIALLY_SCORED',
                'SCORED'
            ]);
            return httpProvider.get(makeUrl(`/api/licenses?sort=endTime&order=desc&practice=true&${statusFilter}`));
        },
        getLatestLicenses: async (size) => {
            const statusFilter = createStatusFilter([
                'STARTED',
                'COMPLETED',
                'LATE_HANGUP',
                'USER_HUNG_UP',
                'ERROR',
                'NOT_SCORABLE',
                'PARTIALLY_SCORED',
                'SCORED',
                'CERTIFICATION_FAILED',
                'CERTIFICATION_REVOKED',
                'CERTIFIED',
                'ABORTED'
            ]);
            return httpProvider.get(makeUrl(`/api/licenses?pageSize=${size}&page=0&sort=endTime&order=desc&practice=false&${statusFilter}`));
        },
        getLicenseDetails: async (id) => httpProvider.get(makeUrl(`/api/licenses/${id}`)),
        getDataForScoreReport: async (id, lang = 'ENG') => httpProvider.get(makeUrl(`/api/licenses/${id}/certificate?language=${lang}`)),
        downloadLicenseCertificate: async (id, language = 'ENG') => httpProvider.get(makeUrl(`/api/licenses/${id}/certificate.pdf?language=${language.toUpperCase()}`), {
            responseType: 'blob'
        }),
        reSendIdVerificationEmail: async (id) => httpProvider.get(makeUrl(`/api/licenses/${id}/id/email`)),
        getLicensesEmailPreview: async (data, id) => httpProvider.post(makeUrl(`/api/licenses/${id}/shares/preview`), data),
        sendShareEmail: async (data, licenseId) => httpProvider.post(makeUrl(`/api/licenses/${licenseId}/shares`), data),
        getOrderHistory: async (page, search) => httpProvider.get(makeUrl('/api/transactions?sort=createdAt&order=desc'), {
            params: {
                page,
                pageSize: SHARES_TABLE_PAGE_SIZE,
                ...(search && { search })
            }
        }),
        getProcessingOrders: async () => httpProvider.get(makeUrl('/api/transactions?sort=createdAt&order=desc&status=PENDING&status=PAID'), {
            params: {
                pageSize: SHARES_TABLE_PAGE_SIZE
            }
        }),
        sendRefundOrder: async (licenseIds) => httpProvider.post(makeUrl('/api/licenses/refunds'), {
            licenseIds
        }),
        getOrderDetails: async (transactionId) => httpProvider.get(makeUrl(`/api/transactions/${transactionId}`)),
        retakeTest: async (licenseId, systemInfo) => httpProvider.post(makeUrl(`/api/licenses/${licenseId}/retake`), systemInfo),
        getLicenseResolutions: async (id) => httpProvider.get(makeUrl(`/api/licenses/${id}/resolutions`)),
        uploadUserId: async (form, licenseId) => httpProvider.post(makeUrl(`/api/licenses/${licenseId}/id/verify`), form),
        uploadUserIdByToken: async (form, token) => httpProvider.post(makeUrl('/api/licenses/id/verify'), form, {
            headers: { Authorization: `Bearer ${token}` }
        }),
        getTokenFromCode: (code) => httpProvider.get(makeUrl(`/api/licenses/id/token/${code}`)),
        checkIfCanUploadId: (token) => httpProvider.get(makeUrl('/api/licenses/id/verify/status'), {
            headers: { Authorization: `Bearer ${token}` }
        }),
        userStartedVerification: (token) => {
            return httpProvider.post(makeUrl('/api/licenses/id/verify/status'), {}, { headers: { Authorization: `Bearer ${token}` } });
        },
        verifyIdStatus: (licenseId) => httpProvider.get(makeUrl(`/api/licenses/${licenseId}/id/status`)),
        startReprocessingIdVerification: (transactionId, manual, token) => httpProvider.post(makeUrl(`/api/licenses/verification/id/${transactionId}/process`), {
            transactionId,
            manual
        }, { headers: { Authorization: `Bearer ${token}` } }),
        getIdData: (licenseId) => httpProvider.get(makeUrl(`/api/licenses/${licenseId}/id/data`)),
        getPIIData: (transactionId, testToken) => httpProvider.get(makeUrl(`/api/licenses/verification/id/${transactionId}/status`), {
            headers: { Authorization: `Bearer ${testToken}` }
        }),
        hasAnyTestScored: async () => {
            const statusFilter = createStatusFilter([
                'COMPLETED',
                'PARTIALLY_SCORED',
                'SCORED',
                'CERTIFICATION_FAILED',
                'CERTIFIED'
            ]);
            const { data } = await httpProvider.get(makeUrl(`/api/licenses?page=0&practice=false${statusFilter}`));
            return new Promise((resolve) => resolve(data?.results.some((item) => item.scores?.length > 0)));
        },
        getBlueJourneyTileData: async () => httpProvider.get(makeUrl(`/api/licenses/latest`)),
        getLicensesByProductIdWithFilters: async (productId, filters) => {
            const serializedFilters = filters ? serialize(filters) : '';
            return httpProvider.get(makeUrl(`/api/licenses?${serializedFilters}&productId=${productId}`));
        },
        canStartAnotherTest: async () => httpProvider.get(makeUrl(`/api/licenses/can-validate`)),
        redeemActivationCode: async (activationCode) => httpProvider.post(makeUrl(`/api/licenses/redeem`), { activationCode }),
        checkActivationCodeRedeemBlocked: async () => httpProvider.get(makeUrl(`/api/licenses/redeem/blocked`)),
        downloadReceipt: async (transactionId) => httpProvider.get(makeUrl(`/api/transactions/${transactionId}/receipt`), {
            responseType: 'blob'
        }),
        getShareableLicenses: async (page = 0, query) => httpProvider.get(makeUrl(`/api/licenses?sort=endTime&order=desc&page=${page}&pageSize=${TESTS_TABLE_PAGE_SIZE}&practice=false&status=CERTIFIED&name=${query || ''}`)),
        getNewOrValidatedLicenses: async (page = 0) => {
            return httpProvider.get(
            // setting pageSize to 1000 since above that value SOP will throw error
            // and there is no pageSize like -1 to show all results
            makeUrl(`/api/licenses?status=NEW&status=VALIDATED&practice=false&pageSize=1000`), {
                params: { page }
            });
        },
        validateLicense: async (licenseId) => {
            return httpProvider.post(makeUrl(`/api/licenses/${licenseId}/validate`), {});
        },
        refreshLicense: async (licenseId) => {
            const { data } = await httpProvider.get(makeUrl(`/api/licenses/${licenseId}/refresh`));
            return data;
        },
        getBlockingApplications: async () => {
            return httpProvider.get(makeUrl(`/api/dictionaries/applications/blocked`));
        },
        uploadUserAvatar: async (licenseId, form) => httpProvider.postForm(makeUrl(`/api/licenses/${licenseId}/image`), form),
        uploadLogs: (guid, events) => httpProvider.post(makeUrl(`/api/testTaker/logs`), { guid, events }),
        getFaqCategories: async (language) => httpProvider.get(makeUrl(`/api/faq/categories/language/${language}`)),
        getFaqQuestions: async (language, categoryId) => httpProvider.get(makeUrl(`/api/faq/questions/category/${categoryId}/language/${language}`)),
        getHelpCenterCategories: async (language) => httpProvider.get(makeUrl(`/api/faq/${language}`)),
        getOrdersByStatus: async (status, { page = 0, pageSize = 10 } = {}) => httpProvider.get(makeUrl(`/api/transactions?order=desc&status=${status}&pageSize=${pageSize}&page=${page}`)),
        getInstitutions: async ({ search, page, pageSize, order, sort }) => {
            const params = new URLSearchParams({
                page: page.toString(),
                pageSize: pageSize.toString(),
                search
            });
            if (order)
                params.append('order', order);
            if (sort)
                params.append('sort', sort);
            return httpProvider.get(makeUrl(`/api/shares/srw/institutions?${params.toString()}`));
        },
        getInstitutionProgramsByInstitutionCode: async ({ page, pageSize, order, sort, institutionCode, search }) => {
            const params = new URLSearchParams({
                search: search,
                page: page.toString(),
                pageSize: pageSize.toString()
            });
            if (order)
                params.append('order', order);
            if (sort)
                params.append('sort', sort);
            return httpProvider.get(makeUrl(`/api/shares/srw/institutions/${institutionCode}/programs?${params.toString()}`));
        }
    };
};
//# sourceMappingURL=client.js.map